/**
 * Iter 145ai — Daily 2.00× Double Slip panel.
 *
 * Shows the platform's curated 3-5 leg accumulator targeting ~2.00
 * combined real-bookmaker odds.  "Easy double the money" surface that
 * pulls picks from the SAME predictions pipeline as everything else.
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../App";
import {
  Coins, ChevronRight, Sparkles, RefreshCcw,
  CheckCircle2, XCircle, Clock as ClockIcon, TrendingUp, MessageCircle,
  History, Wand2, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";
import { countryToFlag } from "../lib/countryFlag";
import MatchAnalysisModal from "./MatchAnalysisModal";
import QuantumCompareBadge, { useQuantumComparison } from "./QuantumCompareBadge";
import { appTodayIso } from "../lib/appTime";

const fmtKick = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch { return "—"; }
};

const outcomeBadge = (o) => {
  if (o === "hit" || o === "won") return { Icon: CheckCircle2, cls: "text-emerald-300 bg-emerald-500/15 border-emerald-500/40", text: o === "won" ? "WON" : "HIT" };
  if (o === "miss" || o === "lost") return { Icon: XCircle, cls: "text-rose-300 bg-rose-500/15 border-rose-500/40", text: o === "lost" ? "LOST" : "MISS" };
  return { Icon: ClockIcon, cls: "text-muted-foreground bg-foreground/5 border-border", text: "PENDING" };
};

export default function DailyDoubleSlipPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisFid, setAnalysisFid] = useState(null);
  // Iter 145ar — regenerate state.
  const [isRegen, setIsRegen] = useState(false);
  const [isManualSlip, setIsManualSlip] = useState(false);

  // Iter 145at — Persist user-regenerated slip across page refreshes.
  // Keyed by today's app-timezone date so the cached slip auto-expires at
  // rollover (we never serve stale yesterday slips on next-day mount).
  const todayKey = () => appTodayIso();
  const STORAGE_KEY = "ndaku_dd_manual";
  // Iter 145au — Per-device track record of user-generated slips
  // (last 30).  Read by /double/history to render a "Your manual
  // slips" section that settles via /api/daily-double/grade-legs.
  const HISTORY_KEY = "ndaku_dd_manual_history";
  const HISTORY_MAX = 30;
  const readCachedManualSlip = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || obj.date !== todayKey() || !obj.slip?.legs?.length) return null;
      return obj.slip;
    } catch { return null; }
  };
  const writeCachedManualSlip = (slip) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ date: todayKey(), slip }),
      );
    } catch { /* private mode / quota — silently fall back to memory */ }
  };
  const clearCachedManualSlip = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };
  // Iter 145au — append to durable history (independent of `STORAGE_KEY`
  // which only tracks today's "current" manual slip).  Dedup by
  // (date + sorted fid signature) so spamming Regenerate doesn't
  // bloat the list with identical-fid permutations.
  const appendToManualHistory = (slip) => {
    try {
      const sig = (slip.legs || [])
        .map((l) => l.fixture_id).filter(Boolean).sort().join(",");
      const entry = {
        date: todayKey(),
        sig,
        generated_at: new Date().toISOString(),
        slip,
      };
      const raw = localStorage.getItem(HISTORY_KEY);
      let arr = [];
      try { arr = raw ? JSON.parse(raw) : []; if (!Array.isArray(arr)) arr = []; } catch { arr = []; }
      // Drop any existing entry with the same date+sig (re-roll of
      // the same combo) so the freshest timestamp wins.
      arr = arr.filter((e) => !(e.date === entry.date && e.sig === entry.sig));
      arr.unshift(entry);
      arr = arr.slice(0, HISTORY_MAX);
      localStorage.setItem(HISTORY_KEY, JSON.stringify(arr));
    } catch { /* graceful degradation */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    // Iter 145at — restore the user's manually-regenerated slip if
    // we have one cached for today.  Falls through to the official
    // daily slot if no cache (or cache stale at UTC rollover).
    const cached = readCachedManualSlip();
    if (cached) {
      setData(cached);
      setIsManualSlip(true);
      setLoading(false);
      return;
    }
    setIsManualSlip(false);
    try {
      const { data } = await axios.get(`${API}/daily-double/today`);
      setData(data);
    } catch { toast.error("Failed to load daily double slip"); }
    finally { setLoading(false); }
  }, []);

  // Iter 145at — Explicit "Reset to official slip" — clears cache too.
  const loadOfficial = useCallback(async () => {
    clearCachedManualSlip();
    setLoading(true);
    setIsManualSlip(false);
    try {
      const { data } = await axios.get(`${API}/daily-double/today`);
      setData(data);
    } catch { toast.error("Failed to load daily double slip"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const regenerate = useCallback(async () => {
    setIsRegen(true);
    // Exclude ONLY the current slip's fixtures so the next click is
    // guaranteed different from what the user sees right now, while
    // still letting them cycle through all valid combinations without
    // exhausting the candidate pool.
    const exclude = (data?.legs || []).map((l) => l.fixture_id).filter(Boolean);
    try {
      const { data: fresh } = await axios.post(
        `${API}/daily-double/regenerate`,
        { exclude_fixture_ids: exclude },
        { timeout: 30000 },
      );
      if (fresh?.status === "live" && (fresh.legs || []).length > 0) {
        setData(fresh);
        setIsManualSlip(true);
        writeCachedManualSlip(fresh);  // Iter 145at — survive refresh.
        appendToManualHistory(fresh);  // Iter 145au — durable track record.
        toast.success(`Fresh ${fresh.n_legs}-leg HT slip @ ${fresh.combined_odds?.toFixed(2)}×`);
      } else {
        // Pool exhausted with the current slip excluded — try once
        // more without excludes so the user always gets *something*
        // fresh (the diversify-RNG ensures variety even with same fids).
        const { data: fallback } = await axios.post(
          `${API}/daily-double/regenerate`,
          { exclude_fixture_ids: [] },
          { timeout: 30000 },
        );
        if (fallback?.status === "live" && (fallback.legs || []).length > 0) {
          setData(fallback);
          setIsManualSlip(true);
          writeCachedManualSlip(fallback);  // Iter 145at — survive refresh.
          appendToManualHistory(fallback);  // Iter 145au — durable track record.
          toast.success(`Fresh ${fallback.n_legs}-leg HT slip @ ${fallback.combined_odds?.toFixed(2)}×`);
        } else {
          toast.error(
            fresh?.reason || "No more HT combinations available — try again later"
          );
        }
      }
    } catch {
      toast.error("Couldn't generate a new slip. Try again in a moment.");
    } finally {
      setIsRegen(false);
    }
  }, [data]);

  const openAnalysis = async (fid) => {
    setAnalysisFid(fid);
    try {
      const { data } = await axios.get(`${API}/predictions/${fid}/analysis`, { timeout: 30000 });
      setAnalysisData(data);
      setAnalysisOpen(true);
    } catch { toast.error("Failed to load match analysis"); }
    finally { setAnalysisFid(null); }
  };

  // Iter 145bh — Quantum side-by-side comparison for each leg.
  // MUST be called unconditionally on every render (React Rules of Hooks),
  // BEFORE any early returns below.  Empty `quantumItems` is fine — the
  // hook is a no-op until items show up.
  const quantumItems = (data?.legs || []).map((l) => ({
    fixture_id: l.fixture_id,
    market_type: l.market,
  })).filter((i) => i.fixture_id && i.market_type);
  const quantumLookup = useQuantumComparison(quantumItems);

  if (loading) {
    return <div className="h-48 rounded-3xl border border-border bg-card/40 animate-pulse mb-8" data-testid="daily-double-skeleton" />;
  }

  if (!data || data.status === "skipped") {
    return null;  // Backend can't compute (no API key) — hide silently.
  }

  if (data.status === "no_slip" || !data.legs?.length) {
    const elitePool = data.elite_pool_preview || [];
    const poolSize = data.elite_pool_size ?? 0;
    const withOdds = data.candidates_with_odds ?? 0;
    return (
      <section
        className="relative rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-background to-background p-6 mb-8"
        data-testid="daily-double-empty"
      >
        <div className="text-center mb-4">
          <Coins className="w-10 h-10 mx-auto mb-3 text-emerald-400/60" />
          <p className="font-display font-bold text-lg mb-1">Daily 2.00× Double · Elite Gate Active</p>
          {data.reason && (
            <p
              className="text-xs text-muted-foreground max-w-xl mx-auto mb-3 leading-relaxed"
              data-testid="daily-double-empty-reason"
            >
              {data.reason}
            </p>
          )}
          {(poolSize > 0 || withOdds > 0) && (
            <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-300/80 mb-3">
              <span data-testid="daily-double-pool-size">
                Elite pool: <span className="text-emerald-300">{poolSize}</span>
              </span>
              <span className="text-muted-foreground/40">·</span>
              <span data-testid="daily-double-with-odds">
                With odds: <span className="text-emerald-300">{withOdds}</span>
              </span>
              {data.best_achievable_combined != null && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span>Best combo: <span className="text-emerald-300">{data.best_achievable_combined}×</span></span>
                </>
              )}
            </div>
          )}
          <button
            onClick={regenerate}
            disabled={isRegen}
            data-testid="daily-double-empty-regenerate"
            className="inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-full border border-emerald-500 bg-emerald-500 text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-60 disabled:cursor-wait"
          >
            <Wand2 className={`w-3.5 h-3.5 ${isRegen ? "animate-spin" : ""}`} />
            {isRegen ? "Generating…" : "Try generating one now"}
          </button>
        </div>
        {elitePool.length > 0 && (
          <div className="mt-4 pt-4 border-t border-emerald-500/15" data-testid="daily-double-elite-preview">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-emerald-300/70 mb-2 text-center">
              Elite Picks Being Considered
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {elitePool.map((p, idx) => (
                <div
                  key={idx}
                  className="rounded-lg border border-border/60 bg-card/50 px-2.5 py-2"
                  data-testid={`daily-double-elite-preview-item-${idx}`}
                >
                  <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-muted-foreground/70 truncate">
                    {p.league?.name}
                  </div>
                  <div className="font-display font-bold text-xs truncate mt-0.5">
                    {p.home_team?.name} <span className="text-muted-foreground/50">vs</span> {p.away_team?.name}
                  </div>
                  <div className="flex items-center justify-between mt-1.5 text-[10px]">
                    <span className="text-foreground/80 font-semibold">{p.market} · {p.prediction}</span>
                    <span className="text-emerald-300 font-extrabold tabular-nums">{p.elite_score != null ? Math.round(p.elite_score) : "–"}</span>
                  </div>
                  <div className="text-[9px] text-muted-foreground/70 mt-0.5 tabular-nums">
                    Hist {p.historical_accuracy != null ? Math.round(p.historical_accuracy) : "–"}% · n{p.sample_size ?? "–"} · Recent {p.recent_20_accuracy != null ? Math.round(p.recent_20_accuracy) : (p.recent_10_accuracy != null ? Math.round(p.recent_10_accuracy) : "–")}%
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    );
  }

  const slipOutcome = outcomeBadge(data.outcome);
  const SlipIcon = slipOutcome.Icon;
  const stake = 100;
  const payout = (stake * (data.combined_odds || 0)).toFixed(2);

  return (
    <section
      className="relative overflow-hidden rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-500/12 via-background to-background p-5 sm:p-7 mb-8"
      data-testid="daily-double-panel"
    >
      <div className="pointer-events-none absolute inset-0 opacity-20" aria-hidden>
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-emerald-400/30 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
      </div>

      <header className="relative flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-start gap-3 min-w-0">
          <div className="h-11 w-11 rounded-2xl bg-gradient-to-br from-emerald-300 via-emerald-400 to-emerald-600 grid place-items-center shrink-0 shadow-lg shadow-emerald-500/30">
            <Coins className="w-5.5 h-5.5 text-emerald-950" strokeWidth={2.5} />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-emerald-300/90">Daily Easy-Double</div>
              {/* Iter 145ar — HT-intelligence-only badge */}
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-amber-500/40 bg-amber-500/15 text-[9px] font-extrabold uppercase tracking-[0.14em] text-amber-300"
                data-testid="daily-double-ht-only-badge"
                title="Halftime intelligence only — every leg is a first-half market"
              >
                <ClockIcon className="w-2.5 h-2.5" />
                HT-ONLY
              </span>
              {isManualSlip && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-sky-500/40 bg-sky-500/15 text-[9px] font-extrabold uppercase tracking-[0.14em] text-sky-300"
                  data-testid="daily-double-manual-badge"
                  title="You generated this slip manually — refresh to return to today's official slate"
                >
                  <Wand2 className="w-2.5 h-2.5" />
                  YOUR PICK
                </span>
              )}
            </div>
            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl truncate">
              {data.combined_odds?.toFixed(2)}× Combined
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1.5 leading-relaxed max-w-2xl">
              {data.n_legs}-leg HT slip · avg {data.avg_confidence}% conviction
              {data.elite_hardened ? (
                <>
                  {" "}· <span className="text-emerald-300 font-bold">Top-20 Elite hardened</span>
                  {data.min_leg_elite_score != null && (
                    <> · min Elite {Math.round(data.min_leg_elite_score)}</>
                  )}
                </>
              ) : null}
              {" "}· real bookmaker odds
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Iter 145ar — User-driven manual regenerate */}
          <button
            onClick={regenerate}
            disabled={isRegen}
            data-testid="daily-double-regenerate"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border border-emerald-500 bg-emerald-500 text-emerald-950 hover:bg-emerald-400 transition-colors disabled:opacity-60 disabled:cursor-wait"
            title="Generate a new confident HT-only easy double"
          >
            <Wand2 className={`w-3.5 h-3.5 ${isRegen ? "animate-spin" : ""}`} />
            <span>{isRegen ? "Generating…" : "Regenerate"}</span>
          </button>
          {isManualSlip ? (
            <button
              onClick={loadOfficial}
              data-testid="daily-double-reset"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card hover:bg-foreground hover:text-background transition-colors"
              title="Back to today's official daily slip"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Reset</span>
            </button>
          ) : (
            <button
              onClick={load}
              data-testid="daily-double-refresh"
              className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-border bg-card hover:bg-foreground hover:text-background transition-colors"
              title="Refresh today's slip"
            >
              <RefreshCcw className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          )}
          <Link
            to="/double/history"
            data-testid="daily-double-history-link"
            className="shrink-0 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500 hover:text-emerald-950 transition-colors"
            title="Track record"
          >
            <History className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Track record</span>
          </Link>
        </div>
      </header>

      {/* Stake → Payout headline */}
      <div className="relative grid grid-cols-3 gap-2 sm:gap-3 mb-5">
        <div className="rounded-xl border border-border bg-foreground/5 p-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold mb-0.5">Stake</div>
          <div className="font-display font-bold text-xl sm:text-2xl tabular-nums">${stake}</div>
        </div>
        <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/15 p-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.16em] text-emerald-300 font-bold mb-0.5">Returns</div>
          <div className="font-display font-extrabold text-xl sm:text-2xl tabular-nums text-emerald-300">${payout}</div>
        </div>
        <div className="rounded-xl border border-border bg-foreground/5 p-3 text-center">
          <div className={`text-[10px] uppercase tracking-[0.16em] font-bold mb-0.5 ${slipOutcome.text === "WON" ? "text-emerald-300" : slipOutcome.text === "LOST" ? "text-rose-300" : "text-muted-foreground"}`}>
            Status
          </div>
          <div className={`font-display font-bold text-base sm:text-lg flex items-center justify-center gap-1 ${slipOutcome.cls.replace("border-", "").replace(" bg-", " ")}`}>
            <SlipIcon className="w-4 h-4" />
            {slipOutcome.text}
          </div>
        </div>
      </div>

      {/* Legs */}
      <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-2.5">
        {data.legs.map((leg, idx) => {
          const out = outcomeBadge(leg.outcome);
          const OutIcon = out.Icon;
          const isLoading = analysisFid === leg.fixture_id;
          return (
            <article
              key={leg.fixture_id}
              onClick={() => openAnalysis(leg.fixture_id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  openAnalysis(leg.fixture_id);
                }
              }}
              className={`group relative rounded-2xl border border-border bg-card hover:border-emerald-500/60 hover:shadow-lg hover:shadow-emerald-500/10 transition-all p-4 cursor-pointer ${isLoading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid={`daily-double-leg-${leg.fixture_id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="inline-flex items-center justify-center h-6 min-w-6 px-1.5 rounded-full bg-emerald-500 text-emerald-950 text-[10px] font-extrabold tabular-nums">
                  L{idx + 1}
                </span>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-md border ${out.cls}`}>
                  <OutIcon className="w-2.5 h-2.5" />
                  {out.text}
                </span>
              </div>

              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-2">
                <span aria-hidden>{countryToFlag(leg.league?.country)}</span>
                <span className="truncate">{leg.league?.name}</span>
                <span className="mx-0.5">·</span>
                <span className="tabular-nums">{fmtKick(leg.kickoff)}</span>
              </div>

              <div className="font-display font-bold text-sm sm:text-base leading-tight mb-2">
                <span className="truncate">{leg.home_team?.name}</span>
                <span className="text-muted-foreground/60 text-xs mx-1.5">vs</span>
                <span className="truncate">{leg.away_team?.name}</span>
              </div>

              <div className="flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                    {leg.market}
                  </div>
                  <div className="font-display font-bold text-base mt-0.5 truncate">{leg.prediction}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-extrabold text-xl tabular-nums leading-none text-emerald-300">
                    {leg.odds?.toFixed(2)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5 tabular-nums">
                    {leg.confidence}% conf
                  </div>
                </div>
              </div>

              {/* Iter 83 — Top-20-Elite (NEW) context stripe: every
                  leg now carries the same historical/recent-form
                  signals users see on the Elite list. */}
              {(leg.elite_score != null || leg.historical_accuracy != null) && (
                <div
                  className="mt-2 pt-2 border-t border-emerald-500/20 grid grid-cols-3 gap-1 text-[10px] font-semibold"
                  data-testid={`daily-double-leg-elite-${leg.fixture_id}`}
                >
                  {leg.elite_score != null && (
                    <div className="text-center">
                      <div className="text-emerald-300 font-display font-extrabold tabular-nums text-sm leading-none">
                        {Math.round(leg.elite_score)}
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground/70 mt-0.5">Elite</div>
                    </div>
                  )}
                  {leg.historical_accuracy != null && (
                    <div className="text-center">
                      <div className="text-foreground font-display font-extrabold tabular-nums text-sm leading-none">
                        {Math.round(leg.historical_accuracy)}%
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground/70 mt-0.5">Hist · n{leg.sample_size}</div>
                    </div>
                  )}
                  {(leg.recent_20_accuracy ?? leg.recent_10_accuracy) != null && (
                    <div className="text-center">
                      <div className="text-foreground font-display font-extrabold tabular-nums text-sm leading-none">
                        {Math.round(leg.recent_20_accuracy ?? leg.recent_10_accuracy)}%
                      </div>
                      <div className="text-[9px] uppercase tracking-[0.1em] text-muted-foreground/70 mt-0.5">
                        Recent {leg.recent_20_accuracy != null ? "20" : "10"}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-2.5 pt-2 border-t border-border/60 flex items-center justify-between text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-400/80 group-hover:text-emerald-300 transition-colors">
                <span className="text-muted-foreground/70 normal-case tracking-normal">
                  Odds via <span className="text-foreground/80 font-mono">{leg.bookmaker}</span>
                </span>
                <span className="inline-flex items-center gap-0.5">
                  Analysis
                  <ChevronRight className="w-3 h-3" />
                </span>
              </div>

              {/* Iter 145bh — Quantum side-by-side comparison */}
              <QuantumCompareBadge
                q={quantumLookup.get(leg.fixture_id, leg.market)}
                testid={`daily-double-leg-quantum-${leg.fixture_id}`}
              />
            </article>
          );
        })}
      </div>

      <div className="relative mt-4 flex items-center gap-2 text-[10px] text-muted-foreground/80">
        <TrendingUp className="w-3 h-3" />
        <span>
          Built from {data.candidates_considered} candidate picks · real bookmaker odds from API-Sports · target {data.target_odds?.toFixed(2)}×
        </span>
      </div>

      <p className="relative text-[10px] text-muted-foreground/70 italic mt-2 max-w-3xl">
        Same predictions, same math, same calibration as the rest of the platform — every leg now cleared through the Top-20 Elite (NEW) hardening gate: HT sample ≥ 25, historical ≥ 82%, shrunk ≥ 80%, recent form ≥ 80%.
      </p>

      {/* Iter 145aj — WhatsApp Daily Drop hint */}
      <div
        className="relative mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/8 px-3 py-2 text-[11px]"
        data-testid="daily-double-whatsapp-hint"
      >
        <MessageCircle className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
        <span className="text-emerald-200/90 font-semibold">
          WhatsApp Daily Drop · 07:05 UTC
        </span>
        <span className="text-muted-foreground">
          —&nbsp;subscribe once on the Halftime page to get this Easy Double + the 25 HT picks delivered straight to your inbox.
        </span>
      </div>

      {analysisOpen && analysisData && (
        <MatchAnalysisModal
          analysis={analysisData}
          onClose={() => { setAnalysisOpen(false); setAnalysisData(null); }}
        />
      )}
    </section>
  );
}
